import { arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { useCallback } from 'react';
import type { ArchiveTenderParams } from '../components/Dialogs/ArchiveTenderDialog';
import type {
  QuickSettings as QuickSettingsType,
  SupportDoc,
} from '../components/TenderDetails/types';
import { getLoadingKey } from '../components/TenderDetails/utils';
import type { AccountDetailsUpdate } from '../services/api';
import { apiService } from '../services/api';
import { firestore } from '../services/firebase';
import type { AISettings } from '../types/ai';
import type { User } from '../types/auth';
import type { ProposalHeading } from '../types/proposal';
import type { Tender, TenderStatus } from '../types/tender';
import { logDocumentActivity } from '../utils/activityLogger';
import { removeRecentTender } from '../utils/recentTenders';

const EMPTY_OBJECT: Record<string, never> = {};

export interface UseTenderActionsProps {
  tender: Tender | null;
  setTender: React.Dispatch<React.SetStateAction<Tender | null>>;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  setLoadingStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  refreshUserDetails: (silent?: boolean) => void;
  navigate: (path: string) => void;
  setArchiveDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useTenderActions = ({
  tender,
  setTender,
  account,
  showSnackbar,
  setLoadingStates,
  refreshUserDetails,
  navigate,
  setArchiveDialogOpen,
}: UseTenderActionsProps) => {
  const handleUpdateTender = async (field: keyof Tender, value: Tender[keyof Tender]) => {
    if (!tender?.id || !account?.uid) {
      return;
    }
    try {
      const tenderDocRef = doc(firestore, 'tenders', tender.id);
      const updates: Record<string, unknown> = { [field]: value };

      if (field === 'memberPermissions' && value) {
        const userIds = Object.keys(value as Record<string, string[]>);
        if (userIds.length > 0) {
          updates.sharedWith = arrayUnion(...userIds);
        }
      }

      await updateDoc(tenderDocRef, updates);
      showSnackbar('Tender updated successfully', 'success');
    } catch (_error) {
      showSnackbar('Failed to update tender', 'error');
      throw _error;
    }
  };

  const handleUpdateTenderStatus = useCallback(
    async (newStatus: TenderStatus) => {
      if (!account?.clientId || !account?.uid || !tender?.id || tender.status === newStatus) {
        return;
      }

      try {
        const tenderDocRef = doc(firestore, 'tenders', tender.id);
        await updateDoc(tenderDocRef, { status: newStatus });
        showSnackbar('Tender status updated.', 'success');
      } catch (_error) {
        showSnackbar('Failed to update tender status.', 'error');
      }
    },
    [account, tender, showSnackbar]
  );

  const handleDownload = async (
    gsURI: string,
    fileTypeIdentifier: string,
    defaultFileName: string
  ) => {
    if (!gsURI) {
      showSnackbar('File path is missing for download.', 'error');
      return;
    }
    const loadingKey = getLoadingKey(tender?.id, 'download', fileTypeIdentifier);
    setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const url = await apiService.getTenderFileDownloadUrl(gsURI);
      if (url) {
        window.open(url, '_blank');

        showSnackbar('Downloading Document', 'success', 2000);
        logDocumentActivity.downloaded({
          type: fileTypeIdentifier,
          fileName: defaultFileName,
          source: `tender_details_dialog/${tender?.id}`,
        });
      } else {
        throw new Error('No download URL received from server.');
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      if (err.response?.status === 403 && err.response?.data?.message) {
        showSnackbar(`⚠️ LIMIT EXCEEDED: ${err.response.data.message}`, 'warning', 7000);
      } else {
        showSnackbar(
          `Failed to download ${defaultFileName || fileTypeIdentifier}: ${err.message}`,
          'error',
          2000
        );
      }
    } finally {
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleSaveAsDefault = async (
    quickSettings: QuickSettingsType,
    setIsSavingDefault: (v: boolean) => void
  ) => {
    if (!quickSettings) {
      return;
    }
    setIsSavingDefault(true);
    try {
      const newAiSettings: AISettings = {
        ...(account?.aiSettings || EMPTY_OBJECT),
        ...quickSettings,
      };

      const update: AccountDetailsUpdate = {
        aiSettings: newAiSettings,
        clientLanguage: account?.clientLanguage as
          | 'US English'
          | 'UK English'
          | 'Australian English',
      };

      const res = await apiService.updateAccountDetails(update);
      if (res.success) {
        showSnackbar('Default settings saved successfully.', 'success');
        refreshUserDetails(true);
      } else {
        showSnackbar(res.message || 'Failed to save default settings.', 'error');
      }
    } catch (_e) {
      showSnackbar('An error occurred while saving defaults.', 'error');
    } finally {
      setIsSavingDefault(false);
    }
  };

  const handleSaveStructureAsDefault = async (
    proposalStructure: ProposalHeading[],
    setIsSavingDefault: (v: boolean) => void
  ) => {
    setIsSavingDefault(true);
    try {
      const updatePayload: AccountDetailsUpdate = {
        clientLanguage: account?.clientLanguage as
          | 'US English'
          | 'UK English'
          | 'Australian English',
      };
      if (proposalStructure) {
        updatePayload.customProposalStructure = proposalStructure;
      }
      const res = await apiService.updateAccountDetails(updatePayload);
      if (res.success) {
        showSnackbar('Default structure saved successfully.', 'success');
        refreshUserDetails(true);
      } else {
        showSnackbar(res.message || 'Failed to save default structure.', 'error');
      }
    } catch (_e) {
      showSnackbar('An error occurred while saving default structure.', 'error');
    } finally {
      setIsSavingDefault(false);
    }
  };

  const handleArchiveTender = async (params: ArchiveTenderParams) => {
    const reason = params.reason;
    if (!tender?.id) {
      return;
    }
    try {
      await apiService.archiveTender(tender.id, reason);
      if (account?.uid) {
        removeRecentTender(account.uid, tender.id);
      }
      showSnackbar('Tender archived successfully.', 'success');
      navigate('/dashboard');
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(err.message || 'Failed to archive tender.', 'error');
    } finally {
      setArchiveDialogOpen(false);
    }
  };

  const handleToggleLibraryOverride = async (docId: string, isActive: boolean) => {
    if (!tender?.id) {
      return;
    }
    const currentOverrides = tender.libraryDocOverrides || EMPTY_OBJECT;
    const updatedOverrides = {
      ...currentOverrides,
      [docId]: {
        isActive,
        activatedBy: account?.email || 'Unknown',
      },
    };
    try {
      await handleUpdateTender('libraryDocOverrides', updatedOverrides);
      setTender((prev: Tender | null) =>
        prev ? { ...prev, libraryDocOverrides: updatedOverrides } : null
      );
      showSnackbar(`Document override set to ${isActive ? 'active' : 'inactive'}`, 'success');
    } catch (_err) {
      showSnackbar('Failed to update library override', 'error');
    }
  };

  const handleCVDelete = async (
    doc: SupportDoc,
    handleDeleteSupportDoc: (d: SupportDoc) => Promise<void>
  ) => {
    const filename = doc.originalFilename || doc.name || doc.filename || doc.id;
    if (!account?.uid || !tender?.id || !filename || !account?.clientId) {
      showSnackbar('Missing critical information for deleting CV.', 'error');
      return;
    }
    const loadingKey = `delete_cv_${filename}`;
    setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      await handleDeleteSupportDoc(doc);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleDownloadCV = async (doc: SupportDoc) => {
    const storageUri = doc.storageUri || doc.gcsPath;
    if (storageUri) {
      handleDownload(storageUri, `cv_input_${doc.originalFilename}`, doc.originalFilename || 'cv');
    }
  };

  const handleResetTender = async (docType: string = 'all') => {
    if (!account?.clientId || !account?.uid || !tender?.id) {
      showSnackbar('Missing critical information.', 'error');
      return;
    }
    const now = new Date();
    const tenderDocRef = doc(firestore, 'tenders', tender.id);
    try {
      const updates: Record<string, Date | string | number> = { lastResetTimestamp: now };
      if (docType === 'alignmentAnalysis' || docType === 'all') {
        updates['alignmentAnalysis.status'] = 'Error';
        updates['alignmentAnalysis.progress'] = 0;
      }
      if (docType === 'response' || docType === 'all') {
        updates['draftResponse.status'] = 'Error';
        updates['draftResponse.progress'] = 0;
      }
      if (docType === 'review' || docType === 'all') {
        updates['reviewResponse.status'] = 'Error';
        updates['reviewResponse.progress'] = 0;
      }
      await updateDoc(tenderDocRef, updates);
      showSnackbar('Generation status reset.', 'success');
    } catch (_error) {
      showSnackbar('Failed to reset status.', 'error');
    }
  };

  const handleUpdateServiceStatus = async (docType: string, newStatus: string) => {
    if (!account?.clientId || !account?.uid || !tender?.id) {
      return;
    }
    try {
      const tenderDocRef = doc(firestore, 'tenders', tender.id);
      const updates: Record<string, string> = {};
      if (docType === 'alignmentAnalysis') {
        updates['alignmentAnalysis.status'] = newStatus;
      } else if (docType === 'response') {
        updates['draftResponse.status'] = newStatus;
      } else if (docType === 'review') {
        updates['reviewResponse.status'] = newStatus;
      } else if (docType === 'compile') {
        updates['compileStatus.status'] = newStatus;
      }
      await updateDoc(tenderDocRef, updates);
    } catch (_error) {
      showSnackbar('Failed to update status.', 'error');
    }
  };

  const handleResetForm = async (supportDoc: SupportDoc) => {
    if (!account?.uid || !tender?.id || !supportDoc.originalFilename) {
      return;
    }
    try {
      // Form could be in library or tender_docs
      let updated = false;
      const collectionsToTry = ['library', 'tender_docs'];
      const docId = supportDoc.id || supportDoc.originalFilename;
      for (const col of collectionsToTry) {
        try {
          const docRef = doc(firestore, 'tenders', tender.id, col, docId as string);
          await updateDoc(docRef, { generationStatus: 'error' });
          updated = true;
          break; // Stop after first successful update
        } catch (_e) {
          // Ignore and try the next collection
        }
      }

      if (updated) {
        showSnackbar(`Reset status for ${supportDoc.originalFilename}`, 'success');
      } else {
        showSnackbar('Failed to reset form status: Document not found', 'error');
      }
    } catch (_e) {
      showSnackbar('Failed to reset form status', 'error');
    }
  };

  const handleResetAdHocService = async (tenderForms: SupportDoc[]) => {
    if (!account?.uid || !tender?.id) {
      return;
    }
    try {
      const resetPromises = tenderForms.map(async (form) => {
        const docId = (form as SupportDoc & { id?: string }).id || form.originalFilename;
        if (docId) {
          const collectionsToTry = ['library', 'tender_docs'];
          for (const col of collectionsToTry) {
            try {
              const formDocRef = doc(
                firestore,
                'tenders',
                tender?.id as string,
                col,
                docId as string
              );
              await updateDoc(formDocRef, { generationStatus: 'error' });
              break; // Stop after first successful update
            } catch (_e) {
              // Ignore and try the next collection
            }
          }
        }
      });
      await Promise.all(resetPromises);
      showSnackbar('Ad-Hoc Generation service reset.', 'success');
    } catch (_e) {
      showSnackbar('Failed to reset Ad-Hoc service', 'error');
    }
  };

  const handleClientLanguageChange = async (language: string) => {
    if (!account?.clientId) {
      return;
    }
    try {
      await apiService.updateAccountDetails({
        clientLanguage: language as 'US English' | 'UK English' | 'Australian English',
      });
      refreshUserDetails();
      showSnackbar('Language setting updated successfully', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showSnackbar(`Failed to update language: ${message}`, 'error');
    }
  };

  return {
    handleUpdateTender,
    handleUpdateTenderStatus,
    handleDownload,
    handleSaveAsDefault,
    handleSaveStructureAsDefault,
    handleArchiveTender,
    handleToggleLibraryOverride,
    handleCVDelete,
    handleDownloadCV,
    handleResetTender,
    handleUpdateServiceStatus,
    handleResetForm,
    handleResetAdHocService,
    handleClientLanguageChange,
  };
};
